import { CAPTION_LAYOUT_OPTIONS, ClassEnum, COLOR_SCHEME_OPTIONS, NAVIGATION_LAYOUT_OPTIONS, SIZE_OPTIONS } from '@/constants/enum';
import { getColor } from '@/helpers';
import { IMainWidgetSettings, IWidgetColor } from '@/types/components/widget';
import { css } from 'styled-components';


export const DayPickerStyled = (widgetSettings: IMainWidgetSettings, widgetColors: IWidgetColor) => css`
  .${ClassEnum.DayPickerContainer}, .${ClassEnum.TimePickerContainer} {
    max-width: 384px;
    display: flex;
    flex-direction: column;
    gap: 4px;
    transition-duration: 200ms;
    animation: fadeIn 200ms ease-in-out;
  }
  .${ClassEnum.DayPickerLabel} {
    width: 100%;
    font-weight: 500;
    text-align: start;
    scroll-margin: 80px;
    line-height: 1.5;
    font-size: ${() => `${widgetSettings.sizes.inputTitleFontSize}px`};
  }
  .${ClassEnum.Calendar} {
    width: 100%;
    border-width: 1px;
    border-style: solid;
    background-color: ${getColor(widgetSettings.colorScheme, widgetColors, 'calendar', 'calendarBackground')};
    border-color: ${getColor(widgetSettings.colorScheme, widgetColors, 'calendar', 'calendarBorder')} !important;
    border-radius: ${ widgetSettings.sizes.borderRadius};
    padding: ${widgetSettings.customizations.alwaysShowCalendar ? undefined : '8px'};
    overflow: hidden;
  }
  .${ClassEnum.CalendarMonths} {
    position: relative;
    display: flex;
    flex-direction: row;
    justify-content: center;
    z-index: 1;
  }
  .${ClassEnum.CalendarMonth} {
    margin: 8px 0;
  }
  .${ClassEnum.CalendarMonthCaption} {
    display: flex;
    align-items: center;
    height: ${widgetSettings.sizes.widgetSize};
    justify-content: ${widgetSettings.customizations.navigationLayout === NAVIGATION_LAYOUT_OPTIONS.FIRST_LAYOUT
      ? 'center'
      : undefined};
    position: ${widgetSettings.customizations.navigationLayout === NAVIGATION_LAYOUT_OPTIONS.FIRST_LAYOUT
      ? 'relative'
      : undefined};
    padding-left: ${widgetSettings.customizations.navigationLayout === NAVIGATION_LAYOUT_OPTIONS.SECOND_LAYOUT
      ? '4px'
      : undefined};
  }
  .${ClassEnum.CalendarCaptionLabel} {
    font-size: 14px;
    font-weight: 500;
    line-height: 24px;
    display: ${widgetSettings.customizations.captionLayout !== CAPTION_LAYOUT_OPTIONS.LABEL ? 'none' : undefined};
    color: ${widgetSettings.colorScheme === COLOR_SCHEME_OPTIONS.DARK ? '#ffffff' : '#1a1a1a'} 
  }
  .${ClassEnum.CalendarNav} {
    position: absolute;
    width: 100%;
    display: flex;
    justify-content: ${widgetSettings.customizations.navigationLayout === NAVIGATION_LAYOUT_OPTIONS.FIRST_LAYOUT
      ? 'center'
      : 'end'};
    align-items: ${widgetSettings.customizations.navigationLayout === NAVIGATION_LAYOUT_OPTIONS.FIRST_LAYOUT
      ? 'start'
      : undefined};
  }
  .${ClassEnum.CalendarMonthGrid} {
    width: 100%;
    border-collapse: collapse;
  }
  .${ClassEnum.CalendarWeekdays} {
    width: 100%;
    display: flex;
    font-size: 14px;
    line-height: 20px;
    justify-content: ${widgetSettings.customizations.showWeekNumber ? 'end' : 'space-between'};
  }
  .${ClassEnum.CalendarWeekday} {
    padding: 8px 16px;
    display: flex;
    justify-content: center;
    font-size: 14px;
    font-weight: 500;
    line-height: 20px;
    width: ${widgetSettings.customizations.alwaysShowCalendar ? SIZE_OPTIONS.LG : SIZE_OPTIONS.XL};
    color: ${getColor(widgetSettings.colorScheme, widgetColors, 'calendar', 'availableDateColor')};
  }
  .${ClassEnum.CalendarWeek} {
    display: flex;
    flex-grow: 1;
    width: 100%;
    margin-top: 4px;
  }
  .${ClassEnum.CalendarDay} {
    position: relative;
    padding: 0;
    font-size: 0.8125rem;
    line-height: 20px;
    font-weight: 400;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    white-space: nowrap;
    color: ${getColor(widgetSettings.colorScheme, widgetColors, 'calendar', 'availableDateColor')};
    border-radius: ${widgetSettings.sizes.borderRadius};
    width: ${widgetSettings.customizations.alwaysShowCalendar ? SIZE_OPTIONS.XXL : SIZE_OPTIONS.XXXL};
    height: ${widgetSettings.customizations.alwaysShowCalendar ? SIZE_OPTIONS.XXL : SIZE_OPTIONS.XXXL};
    &:hover {
      color: #171717;
      background-color: #f5f5f5;
    }
    &:focus-within {
      position: relative;
      z-index: 20;
    }
    &[data-selected='true'] {
      color: ${getColor(widgetSettings.colorScheme, widgetColors, 'calendar', 'selectedDateColor')};
      background-color: ${getColor(widgetSettings.colorScheme, widgetColors, 'calendar', 'selectedDateBackgroundColor')};
      border-top-right-radius: ${widgetSettings.sizes.borderRadius};
      border-bottom-right-radius: ${widgetSettings.sizes.borderRadius};
    }
    &:has([aria-selected].day-outside) {
      background-color: ${getColor(widgetSettings.colorScheme, widgetColors, 'calendar', 'selectedDateBackgroundColor')};
      opacity: 0.5;
    }
    &:has([aria-selected].day-range-end) {
      border-radius: ${widgetSettings.sizes.borderRadius};
    }
    &[area-selected='true'] {
      opacity: 1;
    }
  }
  .${ClassEnum.CalendarDayButton} {
    width: 100%;
    height: 100%;
    padding: 0;
    display: flex;
    place-items: center;
    place-content: center;
    cursor: pointer;
    color: currentColor;
    background-color: transparent;
    background-image: none;
    border-color: transparent;
  }
  .${ClassEnum.CalendarWeekNumber} {
    font-size: 14px;
    line-height: 20px;
    font-weight: 400;
    display: grid;
    place-items: center;
    padding: 4px;
    color: ${getColor(widgetSettings.colorScheme, widgetColors, 'calendar', 'availableDateColor')};
    height: ${widgetSettings.sizes.widgetSize};
  }
  .${ClassEnum.CalendarSelected} {
    background-color: ${getColor(widgetSettings.colorScheme, widgetColors, 'calendar', 'selectedDateBackgroundColor')};
    color: ${getColor(widgetSettings.colorScheme, widgetColors, 'calendar', 'selectedDateColor')};
    &:hover {
      background-color: ${getColor(widgetSettings.colorScheme, widgetColors, 'calendar', 'selectedDateBackgroundColor')};
      color: ${getColor(widgetSettings.colorScheme, widgetColors, 'calendar', 'selectedDateColor')};
    }
    &:focus {
      background-color: ${getColor(widgetSettings.colorScheme, widgetColors, 'calendar', 'selectedDateBackgroundColor')};
      color: ${getColor(widgetSettings.colorScheme, widgetColors, 'calendar', 'selectedDateColor')};
    }
  }
  .${ClassEnum.CalendarToday} {
    background-color: ${widgetSettings.colorScheme === COLOR_SCHEME_OPTIONS.DARK ? '#fff9' : '#f5f5f5'};
    color: ${widgetSettings.colorScheme === COLOR_SCHEME_OPTIONS.DARK ?  '#f5f5f5' : '#171717'} ;
  }
  .${ClassEnum.CalendarOutside} {
    color: ${getColor(widgetSettings.colorScheme, widgetColors, 'calendar', 'unavailableDateColor')};
    opacity: 0.9;
    &[aria-selected='true'] {
      background-color: #f5f5f5e6;
      color: ${getColor(widgetSettings.colorScheme, widgetColors, 'calendar', 'unavailableDateColor')};
      opacity: 0.1;
    }
  }
  .${ClassEnum.CalendarDisabled} {
    color: ${getColor(widgetSettings.colorScheme, widgetColors, 'calendar', 'unavailableDateColor')};
    opacity: 0.5;
    button {
      cursor: default;
    }
  }
  .${ClassEnum.CalendarRangeMiddle} {
    &[aria-selected='true'] {
      background-color: #f5f5f5; // accent
      color: #171717; // accent-foreground
    }
  }
  .${ClassEnum.CalendarHidden} {
    visibility: hidden;
  }
  .${ClassEnum.CalendarNextMonthButton}, .${ClassEnum.CalendarPreviousMonthButton} {
    z-index: 10;
    width: ${SIZE_OPTIONS.XL} !important;
    height: ${SIZE_OPTIONS.XL} !important;
    border-radius: ${widgetSettings.sizes.borderRadius} !important;
    border: none;
    padding: 0;
    box-shadow: none;

    svg {
      width: 20px;
      height: 20px;
      fill: currentColor;
    }

    &:hover {
      background-color: #f5f5f5;
      color: #171717;
    }
  }
  .${ClassEnum.CalendarPreviousMonthButton} {
    ${widgetSettings.customizations.navigationLayout === NAVIGATION_LAYOUT_OPTIONS.FIRST_LAYOUT &&
    'position: absolute; left: 8px; top: 8px;'};
    color: ${widgetSettings.colorScheme === COLOR_SCHEME_OPTIONS.DARK ? '#ffffff' : '#1a1a1a'} 
  }
  .${ClassEnum.CalendarNextMonthButton} {
    ${widgetSettings.customizations.navigationLayout === NAVIGATION_LAYOUT_OPTIONS.FIRST_LAYOUT &&
    'position: absolute; right: 8px; top: 8px;'};
    color: ${widgetSettings.colorScheme === COLOR_SCHEME_OPTIONS.DARK ? '#ffffff' : '#1a1a1a'} 
  }
  .${ClassEnum.CalendarPopoverButton} {
    display: flex;
    align-items: center;
    gap: 8px;
    font-weight: 500;
    font-size: ${() => `${widgetSettings.sizes.inputFontSize}px`};
  }
  .${ClassEnum.CalendarFooter} {
    border-top: 1px solid ${getColor(widgetSettings.colorScheme, widgetColors, 'calendar', 'calendarBorder')} !important;
    color: ${widgetSettings.colorScheme === COLOR_SCHEME_OPTIONS.DARK ? '#ffffff' : '#303030'};
    &:hover {
      background-color:${widgetSettings.colorScheme === COLOR_SCHEME_OPTIONS.DARK ? '#030712' : '#f7f7f7'} ;
      color: ${widgetSettings.colorScheme === COLOR_SCHEME_OPTIONS.DARK ? '#ffffff' : '#303030'};
    }
    > div {
      width: 100%;
      height: 100%;
      display: flex;
      justify-content: center;
      align-items: center;
      padding: 10px;
      cursor: pointer;
    }
  }
`;
